import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, withQuery, money, quantity, date, dateTime } from '../api.js';
import { useAuth } from '../App.jsx';
import BatchEditor from '../components/BatchEditor.jsx';
import GroupPicker from '../components/GroupPicker.jsx';

/** Полетата за цена работят с текст, за да може да се изчисти на празно. */
const numText = (v) => (v == null || v === '' ? '' : String(Number(v)));
const toNum = (s) => (s.trim() === '' ? null : Number(s));

/*
 * Доставната цена идва от партидите, а не от полето по картона.
 *
 * Полето `products.purchase_price` пази каквото е заварила старата
 * програма и е известно остаряло за стотици стоки. Щом има партиди с
 * наличност, среднопретеглената от тях е истинската платена цена —
 * по избор само за даден склад (различни складове може да носят
 * различна доставна цена, партидите им са различни).
 */
function partidnaSrednaCena(batches, warehouseId) {
  const s = (batches ?? []).filter((b) => Number(b.quantity) > 0
    && (warehouseId == null || String(b.warehouse_id) === String(warehouseId)));
  const kolichestvo = s.reduce((a, b) => a + Number(b.quantity), 0);
  if (!kolichestvo) return null;
  const stoinost = s.reduce((a, b) => a + Number(b.quantity) * Number(b.purchase_price ?? 0), 0);
  return Math.round((stoinost / kolichestvo) * 10000) / 10000;
}

const formOf = (p) => ({
  barcode: p.barcode ?? '',
  code: p.code ?? '',
  purchasePrice: numText(p.purchase_price),
  wholesalePrice: numText(p.wholesale_price),
  retailPrice: numText(p.retail_price),
  note: p.note ?? '',
  // Ключът е низ, защото идва и от <select>, и от id-то на характеристиката.
  props: Object.fromEntries((p.properties ?? []).map((x) => [String(x.property_id), x.value])),
});

const samePropsAs = (form, p) => {
  const now = form.props;
  const was = Object.fromEntries((p.properties ?? []).map((x) => [String(x.property_id), x.value]));
  const keys = new Set([...Object.keys(now), ...Object.keys(was)]);
  return [...keys].every((k) => (now[k] ?? '').trim() === (was[k] ?? ''));
};

/**
 * Наличности в дърво, разделен екран: отляво дървото, отдясно подробности
 * за избраното. Подредбата и поведението повтарят старата програма,
 * защото хората я знаят наизуст.
 *
 * Количество се показва само след стока и след партида — на ниво група
 * не значи нищо, защото би сборувало бройки, копия и килограми.
 *
 * Разгънатото се пази тук, а не във всеки възел, за да може търсенето да
 * разтвори дървото до намерената стока.
 */
/**
 * Всички стоки на групата — на страници по 500 (толкова дава сървърът
 * наведнъж). Досега се взимаха само първите 500 и в „Зареждане на тонер
 * касети“ (649 стоки) азбучно всичко след „Samsung CLP310“ липсваше —
 * целият Xerox (05.10.2026).
 */
async function stokiNaGrupa(groupId, warehouseId, nonZeroOnly) {
  const PAGE = 500;
  const items = [];
  for (let offset = 0; ; offset += PAGE) {
    const res = await api.get(withQuery('/api/products', {
      groupId, warehouseId: warehouseId || undefined, inStock: nonZeroOnly, limit: PAGE, offset,
    }));
    items.push(...res.items);
    if (res.items.length < PAGE || items.length >= Number(res.total ?? 0)) return items;
  }
}

export default function StockTree() {
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState([]);
  const [properties, setProperties] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [nonZeroOnly, setNonZeroOnly] = useState(true);
  const [tree, setTree] = useState(null);
  const [error, setError] = useState(null);

  const [reloads, setReloads] = useState(0);
  const reload = () => setReloads((n) => n + 1);

  const [expanded, setExpanded] = useState(() => new Set());
  const [productsByGroup, setProductsByGroup] = useState(() => new Map());
  // Брояч на презарежданията — влиза в ключа на реда, за да се пресъздаде.
  const [sveji, setSveji] = useState(0);
  const [selected, setSelected] = useState(null);   // { kind, product, batch }
  /*
   * Кой е основният склад.
   *
   * Варна е мястото, от което се продава; всичко останало е „не е тук".
   * Търси се по име, а не по номер, защото номерът е случаен; ако някой
   * ден складът се прекръсти, важи първият по подредба.
   */
  const mainWarehouseId = useMemo(() => {
    const varna = warehouses.find((w) => String(w.name).trim().toUpperCase() === 'ВАРНА');
    return String((varna ?? warehouses[0])?.id ?? '');
  }, [warehouses]);

  // Какво се пипа в момента: { kind, group|product }. Панелът се отваря
  // над дървото, а не в друг екран — редът, на който си щракнал, остава
  // пред очите.
  const [action, setAction] = useState(null);

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [found, setFound] = useState(null);
  const highlightRef = useRef(null);

  const { user } = useAuth();
  const canWrite = user.role !== 'auditor';

  /*
   * Действията по избраната стока (дублирай/премести/слей/партиди/
   * изтрий) стоят на реда с търсенето, не при самата стока — операторът
   * тъкмо е избрал стоката там, окото му вече е горе. Затова състоянието
   * им живее тук, а не в ProductDetail: копчетата се рисуват в тулбара,
   * панелите им (сливане, редактор на партиди, потвърждение за триене)
   * си остават при стоката.
   */
  const [merging, setMerging] = useState(false);
  const [redaktira, setRedaktira] = useState(false);
  const [triene, setTriene] = useState(null);
  const [trieBusy, setTrieBusy] = useState(false);

  const selectedProductId = selected?.kind === 'product' ? selected.product.id : null;
  useEffect(() => {
    setMerging(false);
    setRedaktira(false);
    setTriene(null);
  }, [selectedProductId]);

  useEffect(() => {
    api.get('/api/warehouses').then(setWarehouses).catch(() => {});
    api.get('/api/properties').then(setProperties).catch(() => {});
  }, []);

  // За да презареди съдържанието на вече разгънатите групи, без да ги
  // затваря, при обикновена смяна на филтъра „само с наличност" (виж по-долу).
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const structKeyRef = useRef({ warehouseId, reloads });

  useEffect(() => {
    /*
     * Смяна на склад или презареждане отвън разбива самото дърво — тогава
     * всичко се затваря и се чете отначало. Смяната само на „само с
     * наличност" не пипа кои категории са отворени — операторът тъкмо е
     * влязъл в тях; презарежда се само кое се вижда вътре.
     */
    const structural = structKeyRef.current.warehouseId !== warehouseId
      || structKeyRef.current.reloads !== reloads;
    structKeyRef.current = { warehouseId, reloads };

    if (structural) {
      setTree(null);
      setExpanded(new Set());
      setProductsByGroup(new Map());
      setFound(null);
      setSelected(null);
    }

    api.get(withQuery('/api/stock/tree', { warehouseId, nonZeroOnly }))
      .then(setTree)
      .catch((e) => setError(e.message));

    if (!structural) {
      const groupIds = [...expandedRef.current];
      Promise.all(groupIds.map((groupId) => stokiNaGrupa(groupId, warehouseId, nonZeroOnly)
        .then((items) => [groupId, items]).catch(() => [groupId, null])))
        .then((pairs) => {
          setProductsByGroup((m) => {
            const next = new Map(m);
            for (const [groupId, items] of pairs) { if (items) next.set(groupId, items); }
            return next;
          });
        });
    }
  }, [warehouseId, nonZeroOnly, reloads]);

  async function runGroupAction(kind, group) {
    if (kind === 'delete') {
      if (!window.confirm(`Изтриване на групата „${group.name}"?`)) return;
      try {
        await api.del(`/api/product-groups/${group.id}`);
        reload();
      } catch (e) { setError(e.message); }
      return;
    }
    setAction({ kind, group });
  }

  function runProductAction(kind, product) {
    setAction({ kind, product });
  }

  const groupsById = useMemo(() => {
    const map = new Map();
    const walk = (nodes) => nodes.forEach((n) => { map.set(n.id, n); walk(n.children); });
    walk(tree?.groups ?? []);
    return map;
  }, [tree]);

  // Дървото се ползва при търсене, но не бива да е зависимост: при всяко
  // презареждане търсенето би се пуснало пак и би нулирало панела вдясно
  // заедно с недовършеното в него.
  const groupsRef = useRef(groupsById);
  groupsRef.current = groupsById;
  const treeReady = tree !== null;
  // Имало ли е търсене — за да се изчисти дървото, щом полето се изпразни.
  const searchedRef = useRef(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      /*
       * Изтрито търсене (X-ът в полето, Backspace) връща дървото в
       * началото — както бутонът „Изчисти“. Иначе заредените само
       * намерени стоки и отворените клони оставаха и дървото изглеждаше
       * още филтрирано (05.10.2026).
       */
      if (searchedRef.current) {
        searchedRef.current = false;
        setExpanded(new Set());
        setProductsByGroup(new Map());
      }
      setFound(null);
      return;
    }
    if (!treeReady) return;
    searchedRef.current = true;

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(withQuery('/api/products', {
          q, warehouseId: warehouseId || undefined,
          inStock: nonZeroOnly, activeOnly: false, limit: 200,
        }));

        const byGroup = new Map();
        const toOpen = new Set();
        for (const p of res.items) {
          if (!byGroup.has(p.group_id)) byGroup.set(p.group_id, []);
          byGroup.get(p.group_id).push(p);
          let g = groupsRef.current.get(p.group_id);
          while (g) { toOpen.add(g.id); g = g.parentId ? groupsRef.current.get(g.parentId) : null; }
        }

        setProductsByGroup(byGroup);
        setExpanded(toOpen);
        // Същият набор служи и за скриване: при търсене се показва само
        // пътят до намереното, а не цялото дърво с празни клони.
        setFound({
          ids: new Set(res.items.map((p) => p.id)),
          count: res.total,
          groups: toOpen,
        });
        if (res.items.length === 1) select(res.items[0]);
      } catch (e) {
        setError(e.message);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, warehouseId, nonZeroOnly, treeReady]);

  useEffect(() => {
    if (found && highlightRef.current) {
      highlightRef.current.scrollIntoView({ block: 'center' });
    }
  }, [found, productsByGroup]);

  async function toggleGroup(group) {
    const next = new Set(expanded);
    if (next.has(group.id)) {
      next.delete(group.id);
    } else {
      next.add(group.id);
      if (!productsByGroup.has(group.id) && group.own.products > 0) {
        const items = await stokiNaGrupa(group.id, warehouseId, nonZeroOnly);
        setProductsByGroup((m) => new Map(m).set(group.id, items));
      }
    }
    setExpanded(next);
  }

  /**
   * Избраната стока се дозарежда, защото списъкът не носи всичко.
   * Ако е същата, вече показаното остава — иначе панелът би премигвал и
   * недовършената редакция в него би се загубила.
   */
  async function select(product) {
    setSelected((s) => (s?.kind === 'product' && s.product.id === product.id
      ? s
      : { kind: 'product', product, full: null }));
    const full = await api.get(`/api/products/${product.id}`);
    setSelected((s) => (s?.kind === 'product' && s.product.id === product.id && s.full
      ? s
      : { kind: 'product', product, full }));
  }

  function selectBatch(product, batch, full) {
    setSelected({ kind: 'batch', product, batch, full });
  }

  /** След сливане слятата стока изчезва — дървото се презарежда. */
  /*
   * Дървото се презарежда, когато количествата се променят отвън.
   *
   * Ревизията от редактора на партиди мести и наличността, и партидите, а
   * дървото ги е прочело веднъж и си ги пази: на екрана оставаше „6 бр."
   * при вече занулена стока. Редовете на стоките се изхвърлят, за да се
   * прочетат наново, а броячът кара разгънатите партиди да се пресъздадат
   * — иначе всяка от тях пази своя стар списък.
   */
  async function refreshStock() {
    setSveji((n) => n + 1);
    try {
      setTree(await api.get(withQuery('/api/stock/tree', { warehouseId, nonZeroOnly })));
      /*
       * Само презарежда стоките на вече отворените групи — преди
       * изхвърляше целия кеш и разгънатата група оставаше разгъната
       * (иконата не се затваряше), но празна, докато операторът не я
       * затвореше и отвореше пак на ръка. Точно групата, в която е
       * направена корекцията, трябва да остане на екрана каквато е била.
       */
      const groupIds = [...expanded];
      const pairs = await Promise.all(groupIds.map((groupId) => stokiNaGrupa(groupId, warehouseId, nonZeroOnly)
        .then((items) => [groupId, items]).catch(() => null)));
      setProductsByGroup(new Map(pairs.filter(Boolean)));
    } catch { /* дървото си остава каквото е */ }
  }

  function afterMerge(res) {
    setSelected(null);
    setProductsByGroup(new Map());
    setFound(null);
    setQuery(res.target.name);
    api.get(withQuery('/api/stock/tree', { warehouseId, nonZeroOnly }))
      .then(setTree).catch(() => {});
  }

  function clearSearch() {
    setQuery('');
    setFound(null);
    setExpanded(new Set());
    setProductsByGroup(new Map());
  }

  return (
    <>
      <div className="page-head">
        <h1>Наличности</h1>
        <div className="spacer" />
        <Link to="/products">списъчен изглед</Link>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="toolbar">
        <label className="field" style={{ minWidth: 280 }}>
          Търсене на стока
          <div style={{ display: 'flex', gap: 6 }}>
            <input type="search" placeholder="име, каталожен номер или бърз код"
              value={query} onChange={(e) => setQuery(e.target.value)} autoComplete="off" />
            {query && <button onClick={clearSearch}>Изчисти</button>}
          </div>
          {searching && <span className="hint">търсене…</span>}
        </label>

        <label className="field">
          Склад
          <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="">всички складове</option>
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </label>

        <label style={{ display: 'flex', gap: 6, alignItems: 'center', paddingBottom: 7 }}>
          <input type="checkbox" checked={nonZeroOnly}
            onChange={(e) => setNonZeroOnly(e.target.checked)} />
          само с наличност
        </label>

        <div className="spacer" />

        {/*
          * Действията по избраната стока стоят тук, на реда с търсенето —
          * не долу при самата стока. Операторът вече гледа нагоре, докато
          * търси; копчетата се ползват рядко (веднъж седмично), затова са
          * тънки текстови връзки, не тежки копчета, за да не се борят за
          * място с полетата за търсене на същия ред.
          */}
        {selected?.kind === 'product' && selected.full && canWrite && (
          <div className="stock-actions" style={{ display: 'flex', gap: 8, alignItems: 'center', paddingBottom: 7, flexWrap: 'wrap' }}>
            <button title="създай подобна стока"
              onClick={() => navigate(`/products/new?from=${selected.full.id}`)}>дублирай</button>
            <button title="премести в друга група"
              onClick={() => runProductAction('move', selected.full)}>премести</button>
            <button onClick={() => setMerging((m) => !m)}>
              {merging ? 'откажи сливането' : 'слей с друга стока'}
            </button>
            {Number(selected.full.is_batch_tracked) === 1 && (
              <button onClick={() => setRedaktira((r) => !r)}>
                {redaktira ? 'затвори партидите' : 'редакция на партидите'}
              </button>
            )}
            <button className={triene ? '' : 'danger'} onClick={async () => {
              if (triene) { setTriene(null); return; }
              try {
                setTriene(await api.get(`/api/products/${selected.full.id}/usage`));
              } catch (e) { setError(e.message); }
            }}>
              {triene ? 'откажи изтриването' : 'изтрий'}
            </button>
            <Link to={`/products/${selected.full.id}`} className="button">отвори картата</Link>
          </div>
        )}
        {selected?.kind === 'group' && (() => {
          const products = selected.group.own?.products ?? 0;
          const vsichki = selected.group.own?.allProducts ?? products;
          const children = selected.group.children?.length ?? 0;
          const empty = vsichki === 0 && children === 0;
          return (
            <div className="stock-actions" style={{ display: 'flex', gap: 8, alignItems: 'center', paddingBottom: 7, flexWrap: 'wrap' }}>
              {/* Пълният картон на нова стока — със всичко (06.10.2026); досега
                  стигаше се до него само от списъчния изглед. */}
              <button onClick={() => navigate(`/products/new?groupId=${selected.group.id}`)}>
                Нова стока
              </button>
              <button onClick={() => runGroupAction('add', selected.group)}>Нова подгрупа</button>
              <button onClick={() => runGroupAction('rename', selected.group)}>Преименувай</button>
              <button onClick={() => runGroupAction('move', selected.group)}>Премести</button>
              {vsichki > 0 && (
                <button onClick={() => runGroupAction('moveProducts', selected.group)}>
                  Премести стоките
                </button>
              )}
              <button className="danger" disabled={!empty}
                title={empty ? 'Изтриване на празната група'
                  : 'Празна се трие — първо преместете стоките и подгрупите'}
                onClick={() => runGroupAction('delete', selected.group)}>
                Изтрий
              </button>
            </div>
          );
        })()}
      </div>

      <div className="split">
        <div className="card split-tree">
          {/*
            * Избраното изсветлява намереното.
            *
            * При търсене всички съвпадения светват в бледосиньо. Избере
            * ли се едно от тях, останалите губят фона си — иначе на
            * екрана има пет еднакво оцветени реда и не личи кой се
            * гледа отдясно.
            */}
          <div className={`tree${selected ? ' has-selection' : ''}`}>
            {!tree && <div className="empty">Зареждане…</div>}
            {tree?.groups.length === 0 && <div className="empty">Няма наличности</div>}
            {found?.count > 0 && (
              <div className="hint" style={{ padding: '4px 8px' }}>
                показани са само групите с намерени стоки
              </div>
            )}
            {/* Без това екранът остава празен, щом търсенето не намери нищо. */}
            {found?.count === 0 && (
              <div className="empty">
                Няма намерена стока по „{query.trim()}“
              </div>
            )}
            {tree?.groups.filter((g) => !found?.groups || found.groups.has(g.id)).map((g) => (
              <GroupNode
                key={g.id} group={g} depth={0}
                onSelectGroup={(g) => setSelected({ kind: 'group', group: g })}
                expanded={expanded} onToggle={toggleGroup}
                visibleGroups={found?.groups}
                productsByGroup={productsByGroup} mainWarehouseId={mainWarehouseId} sveji={sveji}
                warehouseId={warehouseId}
                highlight={found?.ids} highlightRef={highlightRef}
                selected={selected} onSelect={select} onSelectBatch={selectBatch}
              />
            ))}
          </div>
        </div>

        <div className="split-detail">
          {action && (
            <CatalogAction
              action={action}
              groups={[...groupsById.values()]}
              onClose={() => setAction(null)}
              onDone={async (nova) => {
                setAction(null);
                await reload();
                // Копието се отваря веднага (12.10.2026) — цените и
                // характеристиките се довършват в неговия панел.
                if (nova?.id) await select(nova);
              }}
              onError={setError}
            />
          )}
          {/* Докато е отворено действие, панелът на стоката не стои под
              него: „Запис" там би записал образеца, не копието. */}
          {!action && !selected && (
            <div className="card"><div className="empty">
              Изберете стока или партида от дървото
            </div></div>
          )}
          {!action && selected?.kind === 'product' && (
            <ProductDetail product={selected.product} full={selected.full}
              canWrite={canWrite}
              properties={properties}
              onSaved={(full) => setSelected((s) => (full ? { ...s, full } : s))}
              onStockChanged={refreshStock}
              onDeleted={async () => { setSelected(null); await refreshStock(); }}
              onMerged={(res) => { setMerging(false); afterMerge(res); }}
              merging={merging}
              redaktira={redaktira} setRedaktira={setRedaktira}
              triene={triene} setTriene={setTriene}
              trieBusy={trieBusy} setTrieBusy={setTrieBusy} />
          )}
          {!action && selected?.kind === 'group' && (
            <GroupDetail group={selected.group} />
          )}
          {!action && selected?.kind === 'batch' && (
            <BatchDetail product={selected.product} batch={selected.batch} full={selected.full}
              warehouseId={warehouseId} />
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------
// Дървото
// ---------------------------------------------------------------------

/**
 * Групата: какво има в нея.
 *
 * Действията (нова подгрупа/преименувай/премести/изтрий) стоят горе в
 * тулбара на страницата, при търсенето — не тук, огледално на
 * действията по избраната стока.
 */
function GroupDetail({ group }) {
  const products = group.own?.products ?? 0;
  /*
   * Дървото показва само действащите стоки, но празна група значи празна
   * от всичко. Иначе се получава група с „0 стоки", която отказва да се
   * изтрие заради стоки, които операторът не вижда никъде.
   */
  const vsichki = group.own?.allProducts ?? products;
  const skriti = Math.max(0, vsichki - products);
  const children = group.children?.length ?? 0;
  const empty = vsichki === 0 && children === 0;

  return (
    <div className="card">
      <div className="card-head">📁 {group.name}</div>
      <div className="card-body">
        <div className="form-grid">
          <F k="Стоки в нея" v={skriti > 0 ? `${vsichki} · ${skriti} спрени` : products} />
          <F k="Подгрупи" v={children} />
        </div>
        {!empty && (
          <p className="hint" style={{ marginTop: 10 }}>
            Групата не е празна, затова не се трие. Така стоката не изчезва от
            дървото, без да е махната отникъде другаде.
            {skriti > 0 && (
              <> {skriti === vsichki ? 'Всичките ѝ стоки са' : `${skriti} от стоките ѝ са`}{' '}
                спрени и не се показват в дървото — преместете ги наведнъж с
                „Премести стоките".</>
            )}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Едно кратко прозорче за всички действия по номенклатурата.
 *
 * Дублирането е тук заради най-честия случай: новата стока е като
 * съседната, с друго име. По-лесно е да се вземе тя и да се промени
 * името, отколкото да се попълва всичко от нулата — затова името идва
 * готово и е маркирано, а групата е нейната.
 */
function CatalogAction({ action, groups, onClose, onDone, onError }) {
  const { kind, group, product } = action;
  const [name, setName] = useState(
    kind === 'rename' ? group.name
      : kind === 'duplicate' ? `${product.name} (копие)`
        : ''
  );
  const [target, setTarget] = useState(
    kind === 'moveProducts' ? ''
      : String((kind === 'move' || kind === 'duplicate') && product
        ? product.group_id ?? '' : group?.parent_id ?? '')
  );
  const [note, setNote] = useState(kind === 'duplicate' ? (product?.note ?? '') : '');
  const [busy, setBusy] = useState(false);
  const input = useRef(null);

  useEffect(() => { input.current?.select(); }, []);

  const title = {
    add: 'Нова подгрупа',
    rename: 'Ново име на групата',
    move: product ? 'Преместване на стоката' : 'Преместване на групата',
    moveProducts: 'Преместване на стоките от групата',
    duplicate: 'Нова стока по образец',
  }[kind];

  async function submit() {
    setBusy(true);
    try {
      if (kind === 'add') {
        await api.post('/api/product-groups', { name: name.trim(), parentId: group.id });
      } else if (kind === 'rename') {
        await api.patch(`/api/product-groups/${group.id}`, { name: name.trim() });
      } else if (kind === 'move' && group) {
        await api.patch(`/api/product-groups/${group.id}`,
          { parentId: target ? Number(target) : null });
      } else if (kind === 'move' && product) {
        await api.patch(`/api/products/${product.id}`, { groupId: Number(target) });
      } else if (kind === 'moveProducts') {
        await api.put(`/api/product-groups/${group.id}/move-products`,
          { targetId: Number(target) });
      } else if (kind === 'duplicate') {
        const nova = await api.post(`/api/products/${product.id}/duplicate`,
          { name: name.trim(), groupId: target ? Number(target) : undefined, note });
        onDone(nova);
        return;
      }
      onDone();
    } catch (e) {
      onError(e.message);
      setBusy(false);
    }
  }

  const needsName = kind === 'add' || kind === 'rename' || kind === 'duplicate';
  const needsGroup = kind === 'move' || kind === 'duplicate' || kind === 'moveProducts';
  // Стоките отиват в група — „най-горно ниво" не е адрес за тях.
  const ready = (!needsName || name.trim().length >= 2)
    && (!needsGroup || target || (group && kind !== 'moveProducts'));

  return (
    <div className="card">
      <div className="card-head">{title}</div>
      <div className="card-body">
        {needsName && (
          <label className="field">
            Наименование
            <input ref={input} value={name} autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && ready) submit(); }} />
          </label>
        )}
        {kind === 'duplicate' && (
          <label className="field">
            Бележка
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            <span className="hint">копирана от образеца — поправи я за новата стока. След „Готово" копието се отваря и там се довършват цените и характеристиките.</span>
          </label>
        )}
        {needsGroup && (
          <label className="field">
            Група
            <GroupPicker
              label="— изберете —"
              value={target}
              valueLabel={groups.find((g) => String(g.id) === String(target))?.name}
              exclude={kind === 'moveProducts' && group ? new Set([group.id]) : undefined}
              rootLabel={group && kind === 'move' ? '— най-горно ниво —' : undefined}
              onPick={(id) => setTarget(id == null ? '' : String(id))}
              onClear={() => setTarget('')}
            />
          </label>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
          <button onClick={onClose} disabled={busy}>Откажи</button>
          <button className="primary" onClick={submit} disabled={!ready || busy}>Готово</button>
        </div>
      </div>
    </div>
  );
}

function GroupNode({
  group, depth, expanded, onToggle, visibleGroups, productsByGroup, warehouseId, mainWarehouseId,
  sveji,
  highlight, highlightRef, selected, onSelect, onSelectBatch, onSelectGroup,
}) {
  const open = expanded.has(group.id);
  const products = productsByGroup.get(group.id);

  // При търсене се вървят само клоните, водещи до намереното.
  const children = visibleGroups
    ? group.children.filter((c) => visibleGroups.has(c.id))
    : group.children;
  const hasChildren = children.length > 0 || group.own.products > 0;

  return (
    <>
      {/* Редът само се отваря и избира. Действията по групата са в
          панела вдясно: ползват се веднъж месечно и нямат работа тук,
          където се чете по цял ден. */}
      <div
        className={`tree-row${selected?.kind === 'group' && selected.group.id === group.id
          ? ' tree-selected' : ''}`}
        style={{ paddingLeft: 8 + depth * 16 }}
        onClick={() => { if (hasChildren) onToggle(group); onSelectGroup?.(group); }}
        role="button">
        <span className="tree-toggle">{hasChildren ? (open ? '−' : '+') : ''}</span>
        <span className="tree-icon">📁</span>
        <span className="tree-name">{group.name}</span>
      </div>

      {open && children.map((c) => (
        <GroupNode key={c.id} group={c} depth={depth + 1}
          expanded={expanded} onToggle={onToggle} visibleGroups={visibleGroups}
          productsByGroup={productsByGroup} warehouseId={warehouseId}
          mainWarehouseId={mainWarehouseId} sveji={sveji}
          highlight={highlight} highlightRef={highlightRef}
          selected={selected} onSelect={onSelect} onSelectBatch={onSelectBatch}
          onSelectGroup={onSelectGroup} />
      ))}

      {open && products?.map((p) => (
        <ProductNode key={`${p.id}-${sveji}`} product={p} depth={depth + 1} warehouseId={warehouseId}
          mainWarehouseId={mainWarehouseId}
          highlighted={highlight?.has(p.id)} highlightRef={highlightRef}
          selected={selected} onSelect={onSelect} onSelectBatch={onSelectBatch} />
      ))}
    </>
  );
}

function ProductNode({
  product, depth, warehouseId, highlighted, highlightRef, selected, onSelect, onSelectBatch,
  mainWarehouseId,
}) {
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState(null);
  // Услугата няма партиди — няма какво да се разгъва под нея.
  const tracked = !!Number(product.is_batch_tracked) && Number(product.is_stocked) !== 0;
  const isSelected = selected?.kind === 'product' && selected.product.id === product.id;

  // Партидите от други складове не се крият, а се отбелязват: стоката е
  // налична, но не е там, откъдето се продава.
  const batches = full?.batches ?? null;
  const manyWarehouses = new Set((batches ?? []).map((b) => b.warehouse_id)).size > 1;

  async function toggle(e) {
    e.stopPropagation();
    const next = !open;
    setOpen(next);
    if (next && !full) setFull(await api.get(`/api/products/${product.id}`));
  }

  /*
   * Намерена стока, разпръсната по складове, се разгъва сама.
   *
   * Числото в дървото сборува всички складове и по него не личи, че 30
   * броя стоят във Варна, а 4 — другаде. Точно това обаче решава дали
   * стоката може да се продаде сега, затова партидите се показват без
   * да се пита. Стока на едно място не се разгъва: там няма въпрос.
   */
  useEffect(() => {
    if (!highlighted || !tracked || open) return;
    if (Number(product.warehouses ?? 0) <= 1) return;
    let zhiv = true;
    (async () => {
      const f = full ?? await api.get(`/api/products/${product.id}`);
      if (!zhiv) return;
      setFull(f);
      setOpen(true);
    })();
    return () => { zhiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlighted, product.id]);

  const qty = Number(product.quantity);

  return (
    <>
      <div
        className={`tree-row${highlighted ? ' tree-found' : ''}${isSelected ? ' tree-selected' : ''}`}
        style={{ paddingLeft: 8 + depth * 16 }}
        ref={highlighted ? highlightRef : undefined}
        onClick={() => onSelect(product)}
        role="button"
      >
        <span className="tree-toggle" onClick={tracked ? toggle : undefined}
          style={{ cursor: tracked ? 'pointer' : 'default' }}>
          {tracked ? (open ? '−' : '+') : ''}
        </span>
        <span className="tree-icon">
          {Number(product.is_stocked) === 0 ? '🛠' : (qty > 0 ? '🔴' : '⚪')}
        </span>
        <span className="tree-name">
          {product.name}
          {/*
            * Каталожният номер казва КАКВО е стоката.
            *
            * Наименованието е код по вътрешна подредба —
            * "100HPCE505X/CF280X/719" — и от него не личи за кой принтер
            * е. Каталожният номер го казва с думи: "HP 2055, MF411".
            * Операторът и без това търси по него.
            */}
          {product.code && (
            <span className="muted" style={{ marginLeft: 8, fontWeight: 400 }}>
              {product.code}
            </span>
          )}
          {/* Услугата няма количество и не бива да се чете като „нула
              налични" — тя се продава винаги. */}
          {Number(product.is_stocked) === 0 ? (
            <span className="badge muted" style={{ marginLeft: 6 }}>услуга</span>
          ) : qty !== 0 && (
            <strong className="tree-count"> : {quantity(qty)} {product.unit}</strong>
          )}
          {!Number(product.is_active) && (
            <span className="badge muted" style={{ marginLeft: 6 }}>неактивна</span>
          )}
        </span>

      </div>

      {open && batches?.map((b) => {
        const chosen = selected?.kind === 'batch' && selected.batch.id === b.id;
        const elsewhere = warehouseId && String(b.warehouse_id) !== String(warehouseId);
        return (
          <div
            key={b.id}
            className={`tree-row${chosen ? ' tree-selected' : ''}${elsewhere ? ' tree-elsewhere' : ''}`}
            style={{ paddingLeft: 8 + (depth + 1) * 16 }}
            onClick={() => onSelectBatch(product, b, full)}
            role="button"
          >
            <span className="tree-toggle" />
            <span className="tree-icon">🏷</span>
            <span className="tree-name">
              {b.batch_no}
              <strong className="tree-count"> : {quantity(b.quantity)} {product.unit}</strong>
              {/* От кого е дошла: две партиди от една стока се различават
                  по цена, а цената идва от доставчика. */}
              {b.supplier_name && (
                <span className="muted" style={{ marginLeft: 8 }}>от {b.supplier_name}</span>
              )}
              {elsewhere && (
                <span className="badge warn" style={{ marginLeft: 6 }}>
                  друг склад: {b.warehouse_name}
                </span>
              )}
              {/* Складът се чете от разстояние: розовото значи „има я,
                  но не е във Варна" — тоест не може да се даде веднага. */}
              {!warehouseId && manyWarehouses && (
                <span
                  className={`badge ${String(b.warehouse_id) === String(mainWarehouseId)
                    ? 'muted' : 'wh-other'}`}
                  style={{ marginLeft: 6 }}
                >
                  {b.warehouse_name}
                </span>
              )}
            </span>
          </div>
        );
      })}
      {open && batches?.length === 0 && (
        <div className="tree-row muted" style={{ paddingLeft: 8 + (depth + 1) * 16 }}>
          <span className="tree-toggle" />
          <span className="tree-name">няма партиди с наличност</span>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------
// Панелът отдясно
// ---------------------------------------------------------------------

function ProductDetail({
  product, full, properties, onSaved, onMerged, onStockChanged, onDeleted, canWrite,
  merging, redaktira, setRedaktira, triene, setTriene, trieBusy, setTrieBusy,
}) {
  // Цените и бележката се редактират направо тук — това е екранът, на който
  // хората ги гледат, и е излишно да се минава през картата на стоката.
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);

  // Смяната на името е отделно, незабавно действие — не чака „Запис" на
  // останалите полета и не се бута с тях в едно голямо PATCH.
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);

  // Само при смяна на стоката — иначе собственият ни запис би нулирал
  // формата и потвърждението никога не би се показало.
  useEffect(() => {
    setForm(full ? formOf(full) : null);
    setSaved(false);
    setErr(null);
    setRenaming(false);
  }, [full?.id]);

  if (!full || !form) return <div className="card"><div className="empty">Зареждане…</div></div>;

  /*
   * Доставната цена идва от партидите, а не от полето.
   *
   * В полето стои каквото е заварила старата програма: при
   * 100HPW1490X С ЧИП то показва 15,58 EUR, защото по лайняния склад е
   * останало 30,47 ЛЕВА отпреди смяната на валутата. Истинската цена по
   * последните две доставки е 8,12 и 8,29.
   *
   * Такива са 662 стоки. Затова, щом има партиди с наличност, се показва
   * среднопретеглената от тях — това са парите, които наистина са
   * платени за онова, което лежи на рафта. Полето остава само за стоки
   * без партиди, където няма откъде другаде да се вземе цена.
   */
  const partidnaCena = partidnaSrednaCena(full.batches);

  const dirty = form.barcode !== (full.barcode ?? '')
    || form.code !== (full.code ?? '')
    || form.purchasePrice !== numText(full.purchase_price)
    || form.wholesalePrice !== numText(full.wholesale_price)
    || form.retailPrice !== numText(full.retail_price)
    || form.note !== (full.note ?? '')
    || !samePropsAs(form, full);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setSaved(false);
  };

  const setProp = (id) => (e) => {
    setForm((f) => ({ ...f, props: { ...f.props, [id]: e.target.value } }));
    setSaved(false);
  };

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      const after = await api.patch(`/api/products/${full.id}`, {
        barcode: form.barcode.trim() || null,
        code: form.code.trim() || null,
        purchasePrice: toNum(form.purchasePrice),
        wholesalePrice: toNum(form.wholesalePrice),
        retailPrice: toNum(form.retailPrice),
        note: form.note.trim() || null,
        properties: properties.map((p) => ({
          propertyId: p.id, value: (form.props[String(p.id)] ?? '').trim() || null,
        })),
      });
      // Отговорът на PATCH е самата стока; характеристиките се четат наново.
      after.properties = (await api.get(`/api/products/${full.id}`)).properties;
      // Отговорът носи записаното: „36,50“ се връща като „36.5“ и формата
      // трябва да го приеме, иначе би останала „променена“.
      const merged = { ...full, ...after };
      setForm(formOf(merged));
      onSaved(merged);
      setSaved(true);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  /*
   * Смяната на името е отделно PATCH, веднага — не чака „Запис" на
   * останалите полета. Сървърът вече пази от дублиране в същата група
   * (`PATCH /api/products/:id` връща 409 „В тази група вече има стока с
   * това наименование") — тук само се показва отговорът му.
   */
  async function renameProduct() {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === full.name) { setRenaming(false); return; }
    setRenameBusy(true);
    setErr(null);
    try {
      const after = await api.patch(`/api/products/${full.id}`, { name: trimmed });
      onSaved({ ...full, ...after });
      setRenaming(false);
    } catch (e) {
      setErr(e.message);
    } finally {
      setRenameBusy(false);
    }
  }

  return (
    <>
      <div className="card">
        {/*
          * Името се сменя тук, не като поле сред другите — една промяна,
          * веднага записана, не редче в голямата форма отдолу. Сървърът
          * пази от дублиране в същата група (виж renameProduct); тук се
          * показва само отговорът му, ако откаже.
          */}
        <div className="card-head" style={{ gap: 8 }}>
          {renaming ? (
            <>
              <input autoFocus maxLength={200} value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') renameProduct();
                  if (e.key === 'Escape') setRenaming(false);
                }}
                style={{ fontSize: 16, fontWeight: 600, flex: 1, minWidth: 160 }} />
              <button className="link" disabled={renameBusy} onClick={renameProduct}>запиши</button>
              <button className="link" disabled={renameBusy} onClick={() => setRenaming(false)}>
                откажи
              </button>
            </>
          ) : (
            <>
              <strong style={{ fontSize: 16 }}>{full.name}</strong>
              {canWrite && (
                <button className="link" onClick={() => { setRenameValue(full.name); setRenaming(true); }}>
                  смяна
                </button>
              )}
            </>
          )}
        </div>

        {/*
          * Изтриването има два изхода и те не се решават от човека, а от
          * това дали стоката е участвала в нещо.
          *
          * Не е ли — изчезва наистина. Стои ли по документ, се спира:
          * продажбата отпреди година сочи името, с което е издадена, и
          * това не се пипа по Приложение № 29.
          */}
        {triene && (
          <div className={triene.canDelete ? 'note' : 'error'}
            style={{ margin: '0 14px 12px' }}>
            {triene.canDelete ? (
              <>
                <p style={{ marginTop: 0 }}>
                  <b>„{full.name}"</b> не участва в никакви документи и може да се изтрие.
                  Изтриването е окончателно.
                </p>
                <button className="danger" disabled={trieBusy} onClick={async () => {
                  setTrieBusy(true);
                  try {
                    await api.del(`/api/products/${full.id}`);
                    setTriene(null);
                    await onDeleted?.(full);
                  } catch (e) { setErr(e.message); } finally { setTrieBusy(false); }
                }}>
                  Изтрий окончателно
                </button>
                <button style={{ marginLeft: 8 }} onClick={() => setTriene(null)}>Откажи</button>
              </>
            ) : (
              <>
                <p style={{ marginTop: 0 }}>
                  <b>„{full.name}"</b> не може да се изтрие: {triene.reasons.join(', ')}.
                </p>
                <p style={{ marginTop: 0 }}>
                  Може да се спре — изчезва от списъците и от търсенето, а историята
                  остава цяла.
                </p>
                <button disabled={trieBusy} onClick={async () => {
                  setTrieBusy(true);
                  try {
                    const after = await api.patch(`/api/products/${full.id}`, { isActive: false });
                    setTriene(null);
                    onSaved({ ...full, ...after });
                    await onStockChanged?.();
                  } catch (e) { setErr(e.message); } finally { setTrieBusy(false); }
                }}>
                  Спри стоката
                </button>
                <button style={{ marginLeft: 8 }} onClick={() => setTriene(null)}>Откажи</button>
              </>
            )}
          </div>
        )}
        {merging && <MergePanel product={full} onDone={onMerged} />}
        {redaktira && (
          <BatchEditor
            productId={full.id}
            onClose={() => setRedaktira(false)}
            onSaved={async () => {
              setRedaktira(false);
              await onStockChanged?.();
              /*
               * Стоката се тегли наново, вместо да се подаде празно.
               *
               * Количествата и цените вече не са същите, а панелът има
               * нужда от целия запис — с партидите и наличностите — за
               * да покаже новото състояние.
               */
              try {
                onSaved(await api.get(`/api/products/${full.id}`));
              } catch {
                onSaved(full);
              }
            }}
          />
        )}
        <div className="card-body">
          {/*
            * Бързият код и каталожният номер се поправят направо тук.
            *
            * Номенклатурата е трупана с години и в нея има сгрешени
            * кодове; досега поправката им искаше отваряне на картата, а
            * човекът вече е тук и гледа точно тази стока.
            *
            * „Бърз код" е онова, което в базата се казва `barcode`.
            * Старата програма нямаше такова поле и хората са започнали
            * да ползват баркода за него: пише се „УФР" и излизат всички
            * изпичащи платна. Затова един код стои по цяла група стоки и
            * не се проверява за зает.
            */}
          <div className="form-grid">
            {canWrite ? (
              <>
                <label className="field">
                  Каталожен номер
                  <input value={form.code} onChange={set('code')} maxLength={64}
                    placeholder="няма" autoComplete="off" />
                </label>
                <label className="field">
                  Бърз код
                  <input value={form.barcode} onChange={set('barcode')} maxLength={64}
                    placeholder="няма" autoComplete="off"
                    title="Общ код за търсене; може да се повтаря по няколко стоки" />
                </label>
              </>
            ) : (
              <>
                <F k="Каталожен номер" v={full.code ?? '—'} />
                <F k="Бърз код" v={full.barcode ?? '—'} />
              </>
            )}
          </div>

          <div className="detail-section">Цени</div>
          {canWrite ? (
            <div className="form-grid">
              {partidnaCena !== null ? (
                <div className="field">
                  Доставна
                  <b className="field-static">{money(partidnaCena, full.currency)}</b>
                  <span className="hint">среднопретеглена по партидите с наличност</span>
                </div>
              ) : (
                <label className="field">
                  Доставна
                  <input type="number" step="0.01" min="0" inputMode="decimal"
                    value={form.purchasePrice} onChange={set('purchasePrice')} />
                  <span className="hint">стоката няма партиди с наличност</span>
                </label>
              )}
              <label className="field">
                Продава на едро
                <input type="number" step="0.01" min="0" inputMode="decimal"
                  value={form.wholesalePrice} onChange={set('wholesalePrice')} />
              </label>
              <label className="field">
                Продава на дребно
                <input type="number" step="0.01" min="0" inputMode="decimal"
                  value={form.retailPrice} onChange={set('retailPrice')} />
              </label>
            </div>
          ) : (
            <div className="form-grid">
              <F k="Доставна" v={money(partidnaCena ?? full.purchase_price, full.currency)} />
              <F k="Продава на едро" v={money(full.wholesale_price, full.currency)} />
              <F k="Продава на дребно" v={money(full.retail_price, full.currency)} />
            </div>
          )}

          <div className="detail-section">Бележка</div>
          {canWrite ? (
            <textarea rows={4} value={form.note} onChange={set('note')}
              placeholder="няма бележка" style={{ width: '100%' }} />
          ) : (
            <p className="hint" style={{ whiteSpace: 'pre-line', marginTop: 0 }}>
              {full.note || '—'}
            </p>
          )}

          <div className="detail-section">Характеристики</div>
          {properties.length === 0 ? (
            <p className="hint" style={{ marginTop: 0 }}>Няма заведени характеристики</p>
          ) : canWrite ? (
            <div className="form-grid">
              {properties.map((p) => (
                <label className="field" key={p.id}>
                  {p.name}
                  <input list={`prop-${p.id}`} autoComplete="off"
                    value={form.props[String(p.id)] ?? ''} onChange={setProp(String(p.id))} />
                  {/* Подсказки, а не ограничение — стойността е свободен текст. */}
                  <datalist id={`prop-${p.id}`}>
                    {p.values.map((v) => <option key={v} value={v} />)}
                  </datalist>
                </label>
              ))}
            </div>
          ) : (
            <div className="form-grid">
              {properties.map((p) => (
                <F key={p.id} k={p.name} v={form.props[String(p.id)] || '—'} />
              ))}
            </div>
          )}

          {err && <div className="error" style={{ marginTop: 10 }}>{err}</div>}
          {canWrite && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10 }}>
              <button className="primary" onClick={save} disabled={saving || !dirty}>
                {saving ? 'Записване…' : 'Запис'}
              </button>
              {saved && !dirty && <span className="badge ok">Записано</span>}
            </div>
          )}

          <div className="detail-section">Наличности по складове</div>
          <table>
            <thead>
              <tr>
                <th>Склад</th>
                <th className="num">Наличност</th>
                <th className="num">Доставна</th>
                <th className="num">Дребно</th>
                <th>Място</th>
              </tr>
            </thead>
            <tbody>
              {full.stock.map((s) => {
                const partidnaTuk = partidnaSrednaCena(full.batches, s.warehouse_id);
                return (
                <tr key={s.warehouse_id}>
                  <td>{s.warehouse_name}</td>
                  <td className="num"><strong>{quantity(s.quantity)}</strong> {full.unit}</td>
                  <td className="num"
                    title={partidnaTuk != null
                      ? 'среднопретеглена по партидите с наличност в този склад' : undefined}>
                    {partidnaTuk != null
                      ? <b>{money(partidnaTuk)}</b>
                      : money(s.purchase_price ?? full.purchase_price)}
                  </td>
                  {/*
                    * `stock.retail_price` не се гледа никъде при реална
                    * продажба (`sale-info` използва само цената по
                    * картона) — показването му отделно тук лъжеше, все
                    * едно склад продава на друга цена. Винаги картонът.
                    */}
                  <td className="num">{money(full.retail_price)}</td>
                  <td className="muted">{s.position ?? '—'}</td>
                </tr>
                );
              })}
              {full.stock.length === 0 && (
                <tr><td colSpan={5} className="empty">Стоката не е заведена в склад</td></tr>
              )}
            </tbody>
          </table>

          <div className="detail-section">Основни данни</div>
          <div className="form-grid">
            <F k="Група" v={full.group_name} />
            <F k="Мярка" v={full.unit} />
            <F k="ДДС" v={`${Number(full.vat_rate)} %`} />
            <F k="Партиди" v={Number(full.is_batch_tracked) ? 'да' : 'не'} />
            <F k="Активна" v={Number(full.is_active) ? 'да' : 'не'} />
            <F k="В сайта" v={Number(full.is_web_visible) ? 'да' : 'не'} />
          </div>

          <div className="detail-section">Количества</div>
          <div className="form-grid">
            <F k="Минимално" v={full.min_qty != null ? `${quantity(full.min_qty)} ${full.unit}` : '—'} />
            <F k="Желателно" v={full.max_qty != null ? `${quantity(full.max_qty)} ${full.unit}` : '—'} />
            <F k="Регистрирана" v={date(full.created_at)} />
            <F k="Променена" v={dateTime(full.updated_at)} />
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Сливане на две стоки, които са една и съща.
 *
 * Отворената стока изчезва от употреба, а избраната остава. Тук нарочно
 * пише какво точно се случва: движенията по вече издадени документи НЕ
 * се местят — на кого какво е продадено е реквизит на документа и не се
 * пренаписва. Затова старите продажби ще продължат да сочат старото име.
 */
function MergePanel({ product, onDone }) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (query.trim().length < 2) { setHits([]); return; }
    const t = setTimeout(() => {
      api.get(withQuery('/api/products', { q: query, limit: 8, activeOnly: false }))
        .then((r) => setHits(r.items.filter((p) => p.id !== product.id)))
        .catch(() => setHits([]));
    }, 220);
    return () => clearTimeout(t);
  }, [query, product.id]);

  async function merge() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(`/api/products/${product.id}/merge`, { targetId: chosen.id });
      onDone(res);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }

  return (
    <div className="card-body" style={{ borderBottom: '1px solid var(--border)' }}>
      <p className="hint" style={{ marginTop: 0 }}>
        <strong>„{product.name}“</strong> ще бъде спряна, а наличността,
        партидите, характеристиките, паметта за доставчиците и закачените
        тонер/чип (ако е услуга за зареждане, или ако тя самата е закачена
        към чужда услуга) ще минат към избраната. Вече издадените документи
        остават при старото име — на кого какво е продадено не се пренаписва.
      </p>

      {error && <div className="error">{error}</div>}

      {chosen ? (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>Остава: <strong>{chosen.name}</strong>
            <span className="muted"> · {chosen.group_name}</span></span>
          <button className="link" onClick={() => setChosen(null)}>смени</button>
          <div style={{ flex: 1 }} />
          <button className="danger" onClick={merge} disabled={busy}>
            {busy ? 'Сливане…' : 'Слей окончателно'}
          </button>
        </div>
      ) : (
        <div style={{ position: 'relative', maxWidth: 460 }}>
          <input type="search" autoFocus autoComplete="off" style={{ width: '100%' }}
            placeholder="коя стока остава?"
            value={query} onChange={(e) => setQuery(e.target.value)} />
          {hits.length > 0 && (
            <div className="suggest">
              {hits.map((p) => (
                <div key={p.id} className="suggest-item"
                  onMouseDown={(e) => { e.preventDefault(); setChosen(p); setHits([]); setQuery(''); }}>
                  <div><strong>{p.name}</strong> <span className="muted">{p.group_name}</span></div>
                  {p.note && (
                    <div className="muted" style={{
                      fontSize: 11, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{p.note}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BatchDetail({ product, batch, full, warehouseId }) {
  return (
    <div className="card">
      <div className="card-head">
        Партида {batch.batch_no}
        <div style={{ flex: 1 }} />
        <Link to={`/products/${product.id}`}>картата на стоката</Link>
      </div>
      <div className="card-body">
        <div className="form-grid">
          <F k="Стока" v={product.name} wide />
          <F k="Склад" v={
            <>
              {batch.warehouse_name}
              {warehouseId && String(batch.warehouse_id) !== String(warehouseId) && (
                <span className="badge warn" style={{ marginLeft: 6 }}>друг склад</span>
              )}
            </>
          } />
          <F k="Наличност" v={`${quantity(batch.quantity)} ${product.unit}`} />
          <F k="Доставна цена" v={batch.purchase_price != null
            ? money(batch.purchase_price, batch.currency) : '—'} />
          <F k="Доставчик" v={batch.supplier_name ?? '—'} />
          <F k="Дата на доставката" v={batch.purchase_date ? date(batch.purchase_date) : '—'} />
          <F k="Годност" v={batch.expiry_date ? date(batch.expiry_date) : '—'} />
        </div>

        {full && (
          <>
            <div className="detail-section">Цени на стоката</div>
            <div className="form-grid">
              <F k="Доставна"
                v={money(partidnaSrednaCena(full.batches) ?? full.purchase_price, full.currency)} />
              <F k="Продава на едро" v={money(full.wholesale_price, full.currency)} />
              <F k="Продава на дребно" v={money(full.retail_price, full.currency)} />
            </div>
            <p className="hint">
              Партидата има своя доставна цена — при всяка доставка тя е различна и
              точно затова стоката се води на партиди. „Доставна" горе е среднопретеглената
              от всички партиди с наличност, не полето по картона.
            </p>
          </>
        )}

        <div className="detail-section">Бележка на партидата</div>
        <p className="hint" style={{ whiteSpace: 'pre-line', marginTop: 0 }}>
          {batch.note || '—'}
        </p>

        {full?.note && (
          <>
            <div className="detail-section">Бележка на стоката</div>
            <p className="hint" style={{ whiteSpace: 'pre-line', marginTop: 0 }}>{full.note}</p>
          </>
        )}
      </div>
    </div>
  );
}

function F({ k, v, wide }) {
  return (
    <div style={wide ? { gridColumn: 'span 2' } : undefined}>
      <div className="k">{k}</div>
      <div className="v">{v}</div>
    </div>
  );
}
